import { ChangeEvent } from "react";

import { useTranslator } from "@eo-locale/react";

import { CommonEmailQuestion } from "@/storybook-components";
import { questionNumber, questionTitleWithNumber } from "@/helpers/survey";

import { setSurveyAnswers } from "@/redux/features/survey/slice";
import type { Question } from "@/types";
import { useAppDispatch, useAppSelector } from "@/redux/store/hooks";
import { spacing, textStyles } from "./question-styles";
import { surveyParticipantFieldControl } from "./survey-participant-test-ids";

interface IEmailQuestion {
  question: Question;
  participantFieldId: string;
}

const EmailQuestion = ({
  question,
  participantFieldId
}: IEmailQuestion): JSX.Element => {
  const { translate } = useTranslator();
  const dispatch = useAppDispatch();
  const survey = useAppSelector((state) => state.survey);
  const { answers, errors, saving } = survey;

  const error = errors[question.id] || {};
  const maxLength = question.maxLength || 255;
  const answer = answers[question.id]?.value ?? "";

  const handleChange = (e: ChangeEvent<HTMLInputElement>): void => {
    dispatch(
      setSurveyAnswers({
        questionId: question.id,
        answer: { value: e.target.value }
      })
    );
  };

  return (
    <CommonEmailQuestion
      layoutProps={{ width: "100%" }}
      invalid={!!error.code}
      required={question.required}
      className={error.code ? "ehq-question-error" : null}
      gap={spacing.question}
      questionLabel={questionTitleWithNumber(
        questionNumber(question, survey),
        question
      )}
      questionLabelProps={{ ...textStyles?.label }}
      labelClassName="ehq-surveyQuestionHeading"
      accessibilityTextOfRequired={`* ${translate("common.required")}`}
      answer={answer}
      questionId={question?.id}
      disabled={saving}
      changeEventHandler={handleChange}
      contentClassName="ehq-surveyInputFeild"
      placeholder={translate("survey.emailPlaceholder")}
      size="lg"
      answerProps={{ ...textStyles?.content }}
      footerStackSpacing={spacing.footerStack}
      maxLength={maxLength}
      note={question.notes}
      noteProps={{ ...textStyles?.footer }}
      errorMessage={error?.code && translate(`errors.${error.code}`)}
      errorMessageProps={{ ...textStyles?.error }}
      answerDataTestId={surveyParticipantFieldControl(
        participantFieldId,
        "email-input"
      )}
    />
  );
};

export default EmailQuestion;
